import { useCallback, useEffect, useState } from 'react';

const TITLE_SELECTOR = 'selectable-bar-chart .chart-title';
const TOOLBAR_GAP_PX = 20 + 10;

/**
 * Left offset for the data-box top toolbar so it starts right after the bar chart's title.
 *
 * The title lives inside <selectable-bar-chart>, which commits after the widget's own render, so
 * measuring it during render leaves the toolbar at 0px (over the title) whenever no later render
 * happens to come along. Measure after commit instead, and re-measure whenever the widget's DOM
 * changes (the chart mounting or re-titling) or the window resizes.
 */
const useToolbarLeftPosition = (rootRef, title) => {
  const [leftPosition, setLeftPosition] = useState('0px');

  const measure = useCallback(() => {
    const barTitle = (rootRef.current || document).querySelector(TITLE_SELECTOR);
    return `${title && barTitle ? barTitle.offsetWidth + TOOLBAR_GAP_PX : 0}px`;
  }, [rootRef, title]);

  useEffect(() => {
    let frame = null;
    const update = () => {
      setLeftPosition((prev) => {
        const next = measure();
        return prev === next ? prev : next;
      });
    };
    // Coalesce bursts of DOM mutations (the chart renders its rows in batches) into one layout read.
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };
    update();
    const observer = new MutationObserver(scheduleUpdate);
    if (rootRef.current) observer.observe(rootRef.current, { childList: true, subtree: true });
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [measure, rootRef]);

  return leftPosition;
};

export default useToolbarLeftPosition;
